import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { WeddingVendor, WeddingInquiry } from '../../../types';
import { useChat } from '../../../context/ChatContext';
import { MessageCircle, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

export default function VendorDashboard() {
    const navigate = useNavigate();
    const [vendor, setVendor] = useState<WeddingVendor | null>(null);
    const [inquiries, setInquiries] = useState<WeddingInquiry[]>([]);
    const [loading, setLoading] = useState(true);
    const { createChat, setActiveChatId } = useChat();

    useEffect(() => {
        const fetchVendorData = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                navigate('/');
                return;
            }

            // Fetch vendor profile
            const { data: vendorData } = await supabase
                .from('wedding_vendors')
                .select('*')
                .eq('owner_id', session.user.id)
                .single();

            if (vendorData) {
                setVendor(vendorData);

                // Fetch inquiries
                const { data: inqData } = await supabase
                    .from('wedding_inquiries')
                    .select('*')
                    .eq('vendor_id', vendorData.id)
                    .order('created_at', { ascending: false })
                    .limit(5);
                setInquiries((inqData || []) as WeddingInquiry[]);
            }

            setLoading(false);
        };

        fetchVendorData();
    }, [navigate]);

    const handleOpenChat = async (userId: string) => {
        try {
            const chatId = await createChat(userId);
            setActiveChatId(chatId);
        } catch (error) {
            toast.error("Failed to open chat");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <p className="text-gray-500">Loading...</p>
            </div>
        );
    }

    if (!vendor) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center max-w-md mx-auto p-8">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        Create Your Vendor Profile
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                        You haven't created a vendor profile yet. Get started to showcase your services.
                    </p>
                    <button
                        onClick={() => navigate('/vendor/profile')}
                        className="px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2 mx-auto"
                    >
                        <Plus size={20} />
                        Create Profile
                    </button>
                </div>
            </div>
        );
    }



    // We can access vendor from Outlet context if we wanted, 
    // but here we already have logic to fetch inquiries which depends on vendor ID.
    // For simplicity, let's keep the fetch logic but remove the Sidebar wrapping.

    // Actually, since this is now just the "Overview" pane, we don't need the sidebar code.

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Inquiries</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">{inquiries.length}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Rating</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">{vendor?.rating ? vendor.rating.toFixed(1) : 'N/A'}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Gallery Images</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">{vendor?.images?.length || 0}</p>
                </div>
            </div>

            {/* Recent Inquiries */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h2 className="font-semibold text-gray-900 dark:text-white">Recent Inquiries</h2>
                    <button
                        onClick={() => navigate('/vendor/inquiries')}
                        className="text-sm text-rose-600 hover:text-rose-700"
                    >
                        View All
                    </button>
                </div>
                {inquiries.length > 0 ? (
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                        {inquiries.map((inq) => (
                            <li key={inq.id} className="p-4">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="text-sm text-gray-900 dark:text-white line-clamp-1">
                                            {inq.message || 'No message'}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {inq.event_date ? `Event: ${inq.event_date}` : 'No date specified'}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={`text-xs px-2 py-1 rounded-full ${inq.status === 'pending'
                                                ? 'bg-amber-100 text-amber-700'
                                                : 'bg-green-100 text-green-700'
                                                }`}
                                        >
                                            {inq.status}
                                        </span>
                                        <button
                                            onClick={() => handleOpenChat(inq.user_id)}
                                            className="text-gray-400 hover:text-rose-600 transition-colors"
                                            title="Chat with user"
                                        >
                                            <MessageCircle size={18} />
                                        </button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="p-8 text-center text-gray-500">No inquiries yet.</p>
                )}
            </div>
        </div>
    );
}
